import React from "react";
import useLang from "../../hooks/useLang";
import { currencyCode, currencySymbol } from "../../utils/formatters";

/** One currency's settlement. On PostmanAL there are two — never add them together (W-D16). */
export interface SettlementBreakdownColumn {
	currencyId?: number | null;
	/** Settlement value before the refusals come off. */
	total: number;
	/** The client's unpaid refusal/exchange debt in this currency, as a positive number. */
	refusals: number;
	/** What the client receives: total − refusals. Negative when the refusals exceed it. */
	toPay: number;
}

interface SettlementBreakdownProps {
	columns: SettlementBreakdownColumn[];
	refusalCount: number;
	/** Under the breakdown, e.g. "* storage cost included". */
	footnote?: string;
	/** Bank settlements only: transport the client pays by bank, outside this payout. */
	bankTransport?: {
		value: number;
		/** False on a dual-currency deployment, where this one sum spans both currencies. */
		withSymbol: boolean;
		footnote?: string;
	};
}

const formatAmount = (
	value: number,
	currencyId?: number | null,
	withSymbol = true
) =>
	new Intl.NumberFormat("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value) + (withSymbol ? currencySymbol(currencyId) : "");

/**
 * Total → refusals → to pay, per currency. The refusals are always deducted, so staff see exactly
 * how the amount handed to the client is made up.
 */
const SettlementBreakdown = ({
	columns,
	refusalCount,
	footnote,
	bankTransport,
}: SettlementBreakdownProps) => {
	const { strings } = useLang("clientDetails");

	const multiCurrency = columns.length > 1;
	const gridCols = multiCurrency
		? "grid-cols-[minmax(0,1fr)_auto_auto]"
		: "grid-cols-[minmax(0,1fr)_auto]";
	const hasNegative = columns.some((column) => column.toPay < 0);

	return (
		<div className="flex flex-col gap-3">
			<div className="overflow-hidden rounded-xl border border-gray-200">
				{multiCurrency && (
					<div
						className={`grid ${gridCols} gap-x-8 bg-gray-50 px-5 py-2 text-sm font-medium text-gray-500`}
					>
						<span />
						{columns.map((column) => (
							<span
								key={column.currencyId ?? "local"}
								className="text-right"
							>
								{currencyCode(column.currencyId)}
							</span>
						))}
					</div>
				)}

				<div
					className={`grid ${gridCols} items-center gap-x-8 px-5 py-3 text-base text-gray-800`}
				>
					<span>
						{strings?.settlementValue ?? "Settlement value"}
					</span>
					{columns.map((column) => (
						<span
							key={column.currencyId ?? "local"}
							className="text-right tabular-nums"
						>
							{formatAmount(column.total, column.currencyId)}
						</span>
					))}
				</div>

				<div
					className={`grid ${gridCols} items-center gap-x-8 border-t border-dashed border-gray-200 px-5 py-3 text-base text-gray-800`}
				>
					<span>
						{strings?.breakdownRefusals ?? "Refusals and exchanges"}
						<span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-sm text-gray-600">
							{refusalCount}
						</span>
					</span>
					{columns.map((column) => (
						<span
							key={column.currencyId ?? "local"}
							className="text-right tabular-nums text-red-500"
						>
							{column.refusals > 0 ? "− " : ""}
							{formatAmount(column.refusals, column.currencyId)}
						</span>
					))}
				</div>

				<div
					className={`grid ${gridCols} items-center gap-x-8 border-t border-gray-200 bg-[#4F16C2]/5 px-5 py-4`}
				>
					<span className="text-lg font-semibold text-gray-900">
						{strings?.breakdownToPay ?? "To be paid to the client"}
					</span>
					{columns.map((column) => (
						<span
							key={column.currencyId ?? "local"}
							className={`text-right text-xl font-bold tabular-nums ${
								column.toPay < 0
									? "text-red-600"
									: "text-[#4F16C2]"
							}`}
						>
							{formatAmount(column.toPay, column.currencyId)}
						</span>
					))}
				</div>
			</div>

			{(footnote || hasNegative) && (
				<div className="flex flex-col gap-1 text-sm text-gray-500">
					{footnote && <span>{footnote}</span>}
					{hasNegative && (
						<span className="text-red-600">
							{strings?.breakdownNegativeNote ??
								"The refusals exceed the settlement value. The settlement will be created with a negative value: the client owes the company this amount, so contact the client to collect it."}
						</span>
					)}
				</div>
			)}

			{bankTransport && (
				<div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-gray-50 px-5 py-3">
					<div className="flex flex-col">
						<span className="text-base text-gray-800">
							{strings?.transportOrdervalue}
						</span>
						<span className="text-sm text-gray-500">
							{strings?.willBePaidByBank}
							{bankTransport.footnote &&
								` · ${bankTransport.footnote}`}
						</span>
					</div>
					<span className="text-base font-medium tabular-nums text-gray-800">
						{formatAmount(
							bankTransport.value,
							undefined,
							bankTransport.withSymbol
						)}
					</span>
				</div>
			)}
		</div>
	);
};

export default SettlementBreakdown;
